/**
 * PixelStreamViewer — Phase 5 (Feb 2026 PROMPT_CLOSE_ALL_REMAINING)
 *
 * Renders an Unreal Engine 5 Pixel Streaming session inside a sandboxed
 * iframe. The backend `/api/projects/{id}/photoreal/start` endpoint
 * spins up the GPU VM, returns a `stream_url`, and this component
 * displays the live stream. Status badge and live cost counter are
 * driven by `/photoreal/status` polling.
 *
 * Note: This is a thin shell over Epic's standard PixelStreaming
 * frontend served by the UE5 instance — we trust the iframe sandbox
 * to confine the embedded JS that handles WebRTC negotiation,
 * gamepad / mouse input, and the H.264 video element.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PixelStreamViewer({ projectId, hasModel }) {
  const [status, setStatus] = useState("idle");
  const [streamUrl, setStreamUrl] = useState("");
  const [costUsd, setCostUsd] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const pollRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/projects/${projectId}/photoreal/status`,
        { withCredentials: true },
      );
      setStatus(data.status || "idle");
      setStreamUrl(data.stream_url || "");
      setCostUsd(Number(data.live_cost_usd || 0));
      setElapsedSec(Number(data.elapsed_sec || 0));
    } catch (e) {
      // network blip — keep last known state
    }
  }, [projectId]);

  useEffect(() => {
    refreshStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refreshStatus]);

  const handleStart = async () => {
    setErrorMsg("");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${projectId}/photoreal/start`,
        {},
        { withCredentials: true },
      );
      setStatus(data.status || "starting");
      setStreamUrl(data.stream_url || "");
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(refreshStatus, 5000);
    } catch (e) {
      setErrorMsg(
        e?.response?.data?.detail
          || "Failed to start Unreal stream. The GPU VM may be unavailable.",
      );
    }
  };

  const handleStop = async () => {
    setErrorMsg("");
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/photoreal/stop`,
        {},
        { withCredentials: true },
      );
      if (pollRef.current) clearInterval(pollRef.current);
      setStatus("idle");
      setStreamUrl("");
    } catch (e) {
      setErrorMsg("Failed to stop stream.");
    }
  };

  const isRunning = status === "running" || status === "starting";

  return (
    <div data-testid="pixel-stream-viewer"
         className="bg-zinc-950 text-zinc-100 rounded-lg overflow-hidden
                    border border-zinc-800 flex flex-col"
         style={{ minHeight: 540 }}>
      {/* Header bar */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-zinc-800
                      bg-zinc-900/80 backdrop-blur">
        <span data-testid="photoreal-status-badge"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                          text-xs font-semibold ${
            status === "running" ? "bg-emerald-500/20 text-emerald-300"
            : status === "starting" ? "bg-amber-500/20 text-amber-300"
            : status === "error" ? "bg-rose-500/20 text-rose-300"
            : "bg-zinc-700/40 text-zinc-400"
          }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            status === "running" ? "bg-emerald-400 animate-pulse"
            : status === "starting" ? "bg-amber-400 animate-pulse"
            : "bg-zinc-500"
          }`} />
          {status.toUpperCase()}
        </span>
        <div className="text-xs text-zinc-400 font-mono">
          {elapsedSec > 0 && (
            <span data-testid="photoreal-elapsed">
              {Math.floor(elapsedSec / 60)}:
              {String(elapsedSec % 60).padStart(2, "0")}
            </span>
          )}
        </div>
        <div className="text-xs text-amber-200 font-mono">
          <span data-testid="photoreal-cost">
            ${costUsd.toFixed(2)} USD
          </span>
        </div>
        <div className="ml-auto flex gap-2">
          {!isRunning ? (
            <button
              data-testid="photoreal-start-btn"
              onClick={handleStart}
              disabled={!hasModel}
              className="px-3 py-1 rounded text-xs font-semibold
                         bg-emerald-600 hover:bg-emerald-500
                         disabled:bg-zinc-700 disabled:text-zinc-500
                         disabled:cursor-not-allowed transition">
              ▶ Start Photorealistic
            </button>
          ) : (
            <button
              data-testid="photoreal-stop-btn"
              onClick={handleStop}
              className="px-3 py-1 rounded text-xs font-semibold
                         bg-rose-600 hover:bg-rose-500 transition">
              ■ Stop
            </button>
          )}
        </div>
      </div>

      {/* Viewport */}
      <div className="flex-1 relative bg-black"
           style={{ minHeight: 480 }}>
        {streamUrl && isRunning ? (
          <iframe
            data-testid="photoreal-iframe"
            src={streamUrl}
            title="Unreal Pixel Streaming"
            allow="autoplay; gamepad; xr-spatial-tracking"
            sandbox="allow-scripts allow-same-origin allow-popups"
            className="absolute inset-0 w-full h-full border-0" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center
                          justify-center text-zinc-500 text-sm gap-2 p-6
                          text-center">
            {!hasModel ? (
              <>
                <span className="text-zinc-400 font-semibold">
                  3D model not yet generated
                </span>
                <span>
                  Run BIM pipeline (Sidebar → BIM Pipeline → Generate)
                  to create the FBX before starting the photoreal stream.
                </span>
              </>
            ) : status === "starting" ? (
              <>
                <span className="animate-pulse text-amber-400">
                  Provisioning GPU VM…
                </span>
                <span className="text-xs">
                  Cold start typically takes 60–90 seconds.
                </span>
              </>
            ) : (
              <>
                <span className="text-zinc-400 font-semibold">
                  Photorealistic preview not active
                </span>
                <span className="max-w-md">
                  Click <span className="text-emerald-400">▶ Start</span>{" "}
                  to launch Unreal Engine 5 on an Azure GPU VM. You will
                  be billed per second; stop the stream when finished.
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {errorMsg && (
        <div data-testid="photoreal-error"
             className="px-3 py-2 bg-rose-950/40 border-t border-rose-900
                        text-xs text-rose-300">
          {errorMsg}
        </div>
      )}

      <div className="px-3 py-1.5 border-t border-zinc-800 bg-zinc-900/50
                      text-[10px] text-zinc-500 font-mono flex justify-between">
        <span>Unreal Engine 5 · Pixel Streaming · WebRTC H.264</span>
        <span>Pay-per-second · Manual stop required</span>
      </div>
    </div>
  );
}
